import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Work_Sans } from 'next/font/google';
import ErrorBoundary from '@/components/ErrorBoundary';

// StrapiLivePreview is only imported inside useEffect so Next.js doesn't prefetch
// the chunk on normal (non-preview) pages. The module is downloaded only when a
// user actually enters preview mode, keeping the main bundle lean.
function StrapiLivePreviewLoader() {
  const [Component, setComponent] = useState(null);

  useEffect(() => {
    let mounted = true;
    import('@/components/utilities/StrapiLivePreview').then((mod) => {
      if (mounted) setComponent(() => mod.default);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return Component ? <Component /> : null;
}

// styles
import '@/styles/globals.css';

// Work Sans self-hosted via next/font (no render-blocking Google Fonts request).
// Variable font ships one file covering all weights instead of 9 separate ones.
const workSans = Work_Sans({
  subsets: ['latin', 'latin-ext'],
  variable: '--font-work-sans',
  display: 'swap',
  weight: 'variable',
  style: ['normal', 'italic']
});

const COOKIEBOT_ID = process.env.NEXT_PUBLIC_COOKIEBOT_ID || '35b5ab07-449e-479a-9fbc-d19ed841d0b4';

// Inject Cookiebot uc.js on the first real user interaction (or after 5s as a
// fallback) so it stays out of the LCP path. Tracking scripts in the document
// are still gated by Cookiebot manual mode via data-cookieconsent — nothing
// runs until the user actually consents through the dialog.
function loadCookiebot() {
  if (document.getElementById('Cookiebot')) return;
  const s = document.createElement('script');
  s.id = 'Cookiebot';
  s.src = 'https://consent.cookiebot.com/uc.js';
  s.setAttribute('data-cbid', COOKIEBOT_ID);
  s.setAttribute('data-blockingmode', 'manual');
  s.async = true;
  document.head.appendChild(s);
}

export default function App({ Component, pageProps }) {
  const router = useRouter();

  useEffect(() => {
    let loaded = false;
    const trigger = () => {
      if (loaded) return;
      loaded = true;
      cleanup();
      loadCookiebot();
    };

    const events = ['scroll', 'mousemove', 'touchstart', 'keydown', 'click'];
    const cleanup = () => {
      events.forEach((evt) => window.removeEventListener(evt, trigger));
      clearTimeout(timer);
    };
    events.forEach((evt) => window.addEventListener(evt, trigger, { once: true, passive: true }));
    const timer = setTimeout(trigger, 5000);

    return cleanup;
  }, []);

  useEffect(() => {
    // Handle chunk load errors during Next.js route changes
    const handleRouteChangeError = (err, url) => {
      if (err.name === 'ChunkLoadError' || err.message?.includes('Loading chunk')) {
        // Navigate using window.location to force fresh chunk load
        window.location.href = url;
      }
    };

    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router]);
  return (
    <>
      <Head>
        {/* Inject font CSS variable on :root so body and other elements outside the Component tree can use it */}
        <style>{`:root { --font-work-sans: ${workSans.style.fontFamily}; }`}</style>
      </Head>
      <ErrorBoundary>
        <Component {...pageProps} />
        {pageProps.isPreview && <StrapiLivePreviewLoader />}
      </ErrorBoundary>
    </>
  );
}
